import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Plus, Trash2, GraduationCap } from "lucide-react";
import { toast } from "sonner";
import { api, fmtErr, fmtDate } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useSite } from "../lib/site";
import { PageHeader } from "../components/ModulePage";
import { RagBadge } from "../components/Badges";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { TalkLibrary, isReady } from "../components/TalkLibrary";
import { CourseLibrary, TrainingMatrix } from "../components/CourseLibrary";

const chip = (on) => `rounded-md border px-2.5 py-1 text-xs transition-colors ${on ? "border-rose-500 bg-rose-500/20 text-rose-100" : "border-slate-700 text-slate-400 hover:text-slate-100"}`;

const QuizEditor = ({ quiz, setQuiz }) => (
  <div className="space-y-2" data-testid="quiz-editor">
    <p className="eyebrow">Quiz (optional) - tick the correct answer</p>
    {quiz.map((q, i) => (
      <div key={i} className="space-y-1.5 rounded border border-slate-800 p-2" data-testid={`quiz-q-${i}`}>
        <div className="flex gap-1.5">
          <input className="field h-9" placeholder={`Question ${i + 1}`} value={q.q} onChange={(e) => setQuiz(quiz.map((x, j) => (j === i ? { q: e.target.value, options: x.options, answer: x.answer } : x)))} data-testid={`quiz-q-${i}-text`} />
          <button type="button" onClick={() => setQuiz(quiz.filter((_, j) => j !== i))} className="p-1 text-red-300" data-testid={`quiz-q-${i}-delete`}><Trash2 className="h-4 w-4" /></button>
        </div>
        {q.options.map((o, k) => (
          <div key={k} className="flex items-center gap-2 pl-2">
            <input type="radio" checked={q.answer === k} onChange={() => setQuiz(quiz.map((x, j) => (j === i ? { ...x, answer: k } : x)))} data-testid={`quiz-q-${i}-correct-${k}`} />
            <input className="field h-8" placeholder={`Option ${k + 1}`} value={o} onChange={(e) => setQuiz(quiz.map((x, j) => (j === i ? { q: x.q, answer: x.answer, options: x.options.map((y, m) => (m === k ? e.target.value : y)) } : x)))} data-testid={`quiz-q-${i}-opt-${k}`} />
          </div>
        ))}
      </div>
    ))}
    {quiz.length < 5 && <button type="button" onClick={() => setQuiz([...quiz, { q: "", options: ["", "", ""], answer: 0 }])} className="flex items-center gap-1 text-xs text-slate-300 hover:text-slate-100" data-testid="quiz-add-question"><Plus className="h-3.5 w-3.5" />Add quiz question</button>}
  </div>
);

const NewSession = ({ open, onClose, meta, talks, preset, onCreated }) => {
  const { user } = useAuth();
  const blank = { type: "Toolbox Talk", topic: "", sop_ref: "", date: new Date().toISOString().slice(0, 10), trainer: user?.name || "", languages: ["English"], translator: "", duration_mins: "", description: "", refresher_months: 12, pass_mark: 80 };
  const [f, setF] = useState(blank);
  const [quiz, setQuiz] = useState([]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const applyTalk = (t) => {
    if (!t) { setF({ ...blank, trainer: f.trainer }); setQuiz([]); return; }
    setF({ ...f, talk_id: t.id, type: "Toolbox Talk", topic: t.title, sop_ref: t.sop_ref, languages: t.languages, duration_mins: t.duration_mins, description: t.points.map((p) => p.en).join(" ") });
    setQuiz(t.quiz.map((q) => ({ ...q, options: [...q.options] })));
  };
  useEffect(() => { if (open && preset) { applyTalk(preset); } }, [open, preset]); // eslint-disable-line react-hooks/exhaustive-deps
  const save = async () => {
    try {
      const r = await api.post("/training", { ...f, quiz: quiz.map((q) => ({ ...q, options: q.options.filter((o) => o.trim()) })) });
      toast.success(`${r.data.ref} created - add attendees to sign`);
      setF(blank);
      setQuiz([]);
      onCreated(r.data.id);
    } catch (e) { toast.error(fmtErr(e)); }
  };
  const toggleLang = (l) => setF({ ...f, languages: f.languages.includes(l) ? f.languages.filter((x) => x !== l) : [...f.languages, l] });
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto border-slate-700 bg-ink-2 sm:max-w-2xl" data-testid="new-training-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl uppercase">New training session</DialogTitle></DialogHeader>
        <label className="text-xs text-slate-400">Start from a ready-made multi-language talk (optional)
          <select className="field mt-1" value={f.talk_id || ""} onChange={(e) => applyTalk(talks.find((t) => t.id === e.target.value))} data-testid="training-talk-select">
            <option value="">None - write my own</option>{talks.map((t) => <option key={t.id} value={t.id}>{t.title}</option>)}
          </select>
        </label>
        <div className="grid gap-2 sm:grid-cols-2">
          <select className="field" value={f.type} onChange={set("type")} data-testid="training-type">{meta.types.map((t) => <option key={t}>{t}</option>)}</select>
          <input type="date" className="field" value={f.date} onChange={set("date")} data-testid="training-date" />
          <input className="field sm:col-span-2" placeholder="Topic, e.g. Spill response" value={f.topic} onChange={set("topic")} data-testid="training-topic" />
          <input className="field" placeholder="SOP / document reference" value={f.sop_ref} onChange={set("sop_ref")} data-testid="training-sop" />
          <input className="field" placeholder="Trainer" value={f.trainer} onChange={set("trainer")} data-testid="training-trainer" />
          <input className="field" placeholder="Translator / interpreter (if used)" value={f.translator} onChange={set("translator")} data-testid="training-translator" />
          <input type="number" className="field" placeholder="Duration (minutes)" value={f.duration_mins} onChange={set("duration_mins")} data-testid="training-duration" />
          <label className="text-xs text-slate-400">Refresher every (months, 0 = none)<input type="number" className="field mt-1" value={f.refresher_months} onChange={set("refresher_months")} data-testid="training-refresher" /></label>
          <label className="text-xs text-slate-400">Quiz pass mark %<input type="number" className="field mt-1" value={f.pass_mark} onChange={set("pass_mark")} data-testid="training-pass-mark" /></label>
          <textarea className="field h-20 py-2 sm:col-span-2" placeholder="Content covered" value={f.description} onChange={set("description")} data-testid="training-description" />
        </div>
        <div>
          <p className="eyebrow mb-2">Language(s) delivered in</p>
          <div className="flex flex-wrap gap-1.5">{meta.languages.map((l) => <button key={l} type="button" onClick={() => toggleLang(l)} className={chip(f.languages.includes(l))} data-testid={`training-lang-${l}`}>{l}</button>)}</div>
        </div>
        <QuizEditor quiz={quiz} setQuiz={setQuiz} />
        <div className="flex justify-end gap-2"><Button variant="ghost" onClick={onClose}>Cancel</Button><Button className="bg-rose-600 hover:bg-rose-700" onClick={save} data-testid="training-save">Create session</Button></div>
      </DialogContent>
    </Dialog>
  );
};

const Sessions = ({ rows, onOpen }) => (
  <div className="panel overflow-x-auto">
    <table className="w-full text-sm">
      <thead className="border-b border-slate-800 text-left"><tr>{["Ref", "Date", "Type", "Topic", "Language(s)", "Trainer", "Attendees", "Status"].map((h) => <th key={h} className="eyebrow px-3 py-3 font-normal">{h}</th>)}</tr></thead>
      <tbody>
        {rows.map((s) => (
          <tr key={s.id} onClick={() => onOpen(s.id)} className="cursor-pointer border-b border-slate-800/70 transition-colors hover:bg-slate-800/50" data-testid={`training-row-${s.ref}`}>
            <td className="px-3 py-2 font-mono text-xs text-cyan-300">{s.ref}</td><td className="whitespace-nowrap px-3 py-2">{fmtDate(s.date)}</td><td className="px-3 py-2">{s.type}</td>
            <td className="px-3 py-2 font-semibold">{s.topic}</td><td className="px-3 py-2 text-xs">{s.languages.join(", ")}{s.translator ? ` (translator: ${s.translator})` : ""}</td>
            <td className="px-3 py-2">{s.trainer}</td><td className="px-3 py-2">{s.attendees.length}{s.attendees.some((a) => a.passed === false) && <span className="ml-1 text-xs text-red-400">({s.attendees.filter((a) => a.passed === false).length} retrain)</span>}</td>
            <td className="px-3 py-2"><RagBadge status={s.status === "closed" ? "green" : "amber"} label={s.status} /></td>
          </tr>
        ))}
        {!rows.length && <tr><td colSpan={8} className="px-3 py-10 text-center text-slate-500">No training recorded yet.</td></tr>}
      </tbody>
    </table>
  </div>
);

const People = () => {
  const { site } = useSite();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  useEffect(() => { if (site) api.get("/training/people").then((r) => setRows(r.data)); }, [site]);
  const shown = rows.filter((p) => !q || p.name.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="space-y-3" data-testid="training-people">
      <input className="field max-w-sm" placeholder="Search people..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="training-people-search" />
      {shown.map((p) => (
        <div key={p.name} className="panel p-4" data-testid={`training-person-${p.name}`}>
          <p className="mb-2 font-semibold">{p.name} <span className="text-xs font-normal text-slate-400">{p.records.length} record(s)</span></p>
          <div className="space-y-1 text-xs">
            {p.records.map((r) => (
              <div key={r.session_id} className="flex flex-wrap items-center gap-2 border-t border-slate-800/70 pt-1">
                <span className="font-mono text-cyan-300">{r.ref}</span><span>{fmtDate(r.date)}</span><span className="font-semibold">{r.topic}</span><span className="text-slate-400">in {r.language}</span>
                {r.score != null && <span className={r.passed ? "text-emerald-400" : "text-red-400"}>quiz {r.score}%</span>}
                {r.passed === false ? <RagBadge status="red" label="retraining needed" /> : r.refresher_due && <RagBadge status={r.rag} label={`refresher ${fmtDate(r.refresher_due)}`} />}
              </div>
            ))}
          </div>
        </div>
      ))}
      {!shown.length && <p className="text-sm text-slate-500">No people trained yet.</p>}
    </div>
  );
};

export default function Training() {
  const { can } = useAuth();
  const { site } = useSite();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);
  const [open, setOpen] = useState(false);
  const [talks, setTalks] = useState([]);
  const [preset, setPreset] = useState(null);
  const [tab, setTab] = useState(["people", "talks", "courses", "matrix"].includes(params.get("tab")) ? params.get("tab") : "courses");
  const load = useCallback(() => api.get("/training").then((r) => setRows(r.data)), []);
  useEffect(() => { if (site) { load(); api.get("/training/meta").then((r) => setMeta(r.data)); } }, [site, load]);
  const loadTalks = useCallback(() => api.get("/training/talks").then((r) => setTalks(r.data)).catch(() => {}), []);
  useEffect(() => { loadTalks(); }, [loadTalks]);
  const pending = talks.some((t) => t.status === "generating");
  useEffect(() => { if (!pending) return undefined; const i = setInterval(loadTalks, 6000); return () => clearInterval(i); }, [pending, loadTalks]);
  const ready = talks.filter((t) => isReady(t) && t.status !== "draft");
  const close = () => { setOpen(false); setPreset(null); };
  return (
    <div data-testid="training-page">
      <PageHeader eyebrow={site?.name} title="Training & Toolbox Talks" description="Ready-made courses workers complete on their phone with read-aloud, a quiz, signature and certificate, plus supervisor-led sessions and toolbox talks with the language delivered and each attendee's signed confirmation.">
        {can("training", "check") && meta && <Button onClick={() => setOpen(true)} className="tap bg-rose-600 hover:bg-rose-700" data-testid="new-training-btn"><GraduationCap className="mr-1 h-4 w-4" />New session</Button>}
      </PageHeader>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-4 h-auto flex-wrap justify-start bg-slate-900"><TabsTrigger value="courses" data-testid="tab-courses">Course library</TabsTrigger><TabsTrigger value="sessions" data-testid="tab-sessions">Sessions</TabsTrigger><TabsTrigger value="talks" data-testid="tab-talks">Talk library</TabsTrigger><TabsTrigger value="people" data-testid="tab-people">People</TabsTrigger>{can("training", "manage") && <TabsTrigger value="matrix" data-testid="tab-matrix">Training matrix</TabsTrigger>}</TabsList>
        <TabsContent value="courses"><CourseLibrary /></TabsContent>
        <TabsContent value="matrix">{can("training", "manage") && <TrainingMatrix />}</TabsContent>
        <TabsContent value="sessions"><Sessions rows={rows} onOpen={(id) => nav(`/training/${id}`)} /></TabsContent>
        <TabsContent value="talks"><TalkLibrary talks={talks} canStart={can("training", "check") && !!meta} canManage={can("training", "manage")} onSaved={loadTalks} onStart={(t) => { setPreset(t); setOpen(true); }} /></TabsContent>
        <TabsContent value="people"><People /></TabsContent>
      </Tabs>
      {meta && <NewSession open={open} onClose={close} meta={meta} talks={ready} preset={preset} onCreated={(id) => { close(); nav(`/training/${id}`); }} />}
    </div>
  );
}
